import { useEffect, useState } from 'react';

import type { Channel, StreamChat } from 'stream-chat';

import { toConciergeChannelID } from '@/utils/chat';
import { logger } from '@/services/logger';

type TMemberChannel = {
  channel: Channel | null;
  isPending: boolean;
};

const findMemberChannel = async (client: StreamChat, memberID: string): Promise<Channel | null> => {
  const found = await client.queryChannels(
    { type: 'messaging', id: toConciergeChannelID(memberID) },
    [],
    { limit: 1, state: true, watch: true },
  );

  return found[0] ?? null;
};

const useMemberChannel = (client: StreamChat | null, memberID: string, isEnabled: boolean = true): TMemberChannel => {
  const [channel, setChannel] = useState<Channel | null>(null);
  const [isPending, setIsPending] = useState(true);

  useEffect(() => {
    if (!client?.userID || !isEnabled || memberID.length === 0) {
      setChannel(null);
      setIsPending(false);

      return undefined;
    }

    let isActive = true;

    setIsPending(true);

    const load = async () => {
      try {
        const match = await findMemberChannel(client, memberID);

        if (isActive) {
          setChannel(match);
          setIsPending(false);
        }
      } catch (error) {
        logger.error('useMemberChannel', error);

        if (isActive) {
          setIsPending(false);
        }
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [client, memberID, isEnabled]);

  return { channel, isPending };
};

export type {
  TMemberChannel,
};
export {
  useMemberChannel,
};
